// src/pages/Register.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { onValue, ref, set as firebaseSet } from "firebase/database";
import PinInput from "../components/PinInput";
import { useLanguage } from "../i18n";
import { authErrorToKey } from "../utils/authErrors";
import AppLogo from "../components/AppLogo";
import { buildUserProfilePatch, normalizeLanguage } from "../utils/userProfile";
import {
  taxonomyExamsForState,
  taxonomyStatesWithActiveExams,
} from "../utils/taxonomy";

export default function Register() {
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => {
    const prefill = location.state?.email;
    return (prefill ? String(prefill) : "").trim().toLowerCase();
  });
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [stateName, setStateName] = useState("");
  const [exam, setExam] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("en");
  const [taxonomyData, setTaxonomyData] = useState({});
  const [states, setStates] = useState([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();
  const incomingMessage = location.state?.message || "";
  const { lang, t } = useLanguage();

  useEffect(() => {
    setPreferredLanguage(normalizeLanguage(lang) || "en");
  }, [lang]);

  useEffect(() => {
    const taxonomyRef = ref(db, "taxonomy");
    return onValue(taxonomyRef, (snap) => {
      const taxonomy = snap.val() || {};
      setTaxonomyData(taxonomy);
      const nextStates = taxonomyStatesWithActiveExams(taxonomy);
      setStates(nextStates);
      setTaxonomyLoading(false);
    });
  }, []);

  const exams = useMemo(
    () => taxonomyExamsForState(taxonomyData, stateName),
    [taxonomyData, stateName]
  );

  useEffect(() => {
    if (!stateName) return;
    if (!states.includes(stateName)) setStateName("");
  }, [states, stateName]);

  useEffect(() => {
    if (!exam) return;
    if (!exams.includes(exam)) setExam("");
  }, [exams, exam]);

  // 🔒 Allow only numbers + limit length
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // remove non-digits
    if (value.length <= 10) {
      setMobile(value);
    }
  };

  const handlePinChange = (next) => {
    const value = String(next || "").replace(/\D/g, "");
    setPin(value.slice(0, 6));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError("");

    const nameTrim = name.trim();
    const emailTrim = email.trim().toLowerCase();

    if (!nameTrim) {
      setFormError("Please enter your name");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setFormError(t("auth_invalid_email"));
      return;
    }

    if (mobile.length !== 10) {
      setFormError("Mobile number must be exactly 10 digits");
      return;
    }

    if (pin.length !== 6) {
      setFormError(t("auth_pin_6"));
      return;
    }

    const cleanExam = exam.trim();
    const cleanState = stateName.trim();
    const cleanPreferredLanguage = normalizeLanguage(preferredLanguage);

    if (!cleanExam) {
      setFormError("Please select exam");
      return;
    }

    if (!cleanState) {
      setFormError("Please select state");
      return;
    }

    if (!cleanPreferredLanguage) {
      setFormError("Please choose preferred language");
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        emailTrim,
        pin
      );

      const userId = userCredential.user.uid;

      await firebaseSet(ref(db, `users/${userId}`), {
        userName: nameTrim,
        userEmail: emailTrim,
        mobile,
        userPlan: "free",
        planStartDate: new Date().toISOString(),
        ...buildUserProfilePatch({
          exam: cleanExam,
          state: cleanState,
          preferredLanguage: cleanPreferredLanguage,
        }),
      });

      navigate("/home");
    } catch (err) {
      if (err?.code === "auth/email-already-in-use") {
        setFormError(t("auth_email_in_use"));
        return;
      }
      setFormError(t(authErrorToKey(err)));
    }
  };

  return (
    <div className="container auth-page">
      <div className="auth-logo-row">
        <AppLogo size={72} />
      </div>
      <h2 className="page-title">{t("register_title")}</h2>
      {incomingMessage ? (
        <div
          className="card"
          style={{
            marginBottom: 12,
            background: "#fff7ed",
            border: "1px solid #fed7aa",
          }}
        >
          <strong>{incomingMessage}</strong>
        </div>
      ) : null}
      {formError ? (
        <div
          className="card"
          style={{
            marginBottom: 12,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#7f1d1d",
          }}
        >
          <strong>{formError}</strong>
        </div>
      ) : null}
      <form onSubmit={handleRegister}>
        <div className="form-row">
          <input
            type="text"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="form-row">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-row">
          <input
            type="tel"
            placeholder="Mobile (10 digits)"
            value={mobile}
            onChange={handleMobileChange}
            maxLength={10}
            inputMode="numeric"
            pattern="[0-9]{10}"
            autoComplete="tel"
            required
          />
        </div>

        <div className="form-row">
          <select
            value={stateName}
            onChange={(e) => setStateName(e.target.value)}
            disabled={taxonomyLoading}
            required
          >
            <option value="">{taxonomyLoading ? "Loading states..." : "Select State"}</option>
            {states.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <select
            value={exam}
            onChange={(e) => setExam(e.target.value)}
            disabled={taxonomyLoading || !stateName}
            required
          >
            <option value="">{taxonomyLoading ? "Loading exams..." : "Select Exam"}</option>
            {exams.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <select
            value={preferredLanguage}
            onChange={(e) => setPreferredLanguage(e.target.value)}
            required
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
          </select>
        </div>

        <div className="form-row">
          <div style={{ marginBottom: 8, fontWeight: 700, color: "#334155" }}>
            PIN (6 digits)
          </div>
          <PinInput
            value={pin}
            onChange={handlePinChange}
            length={6}
            autoFocus={false}
            name="pin"
            autoComplete="new-password"
          />
          <input type="hidden" value={pin} required />
        </div>

        <button type="submit">Register</button>
      </form>

      <p className="center" style={{ marginTop: "12px" }}>
        Already have an account?{" "}
        <button
          type="button"
          className="secondary small"
          onClick={() => navigate("/login")}
          style={{ marginLeft: 8 }}
        >
          Login
        </button>
      </p>

      <div className="auth-legal-mini">
        By registering you agree to our{" "}
        <Link to="/terms">Terms</Link> &amp;{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>.{" "}
        <Link to="/refund-policy">Refunds</Link> ·{" "}
        <Link to="/contact">Contact</Link>
      </div>
    </div>
  );
}
